import type * as THREE from 'three';
import type {
    SketchArcDefinition,
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseDefinition,
    SketchEllipseObject,
    SketchLineObject,
    SketchParabolaObject,
    SketchPointObject,
    SketchSplineObject,
    SketchStyleSplineObject,
} from '../../mesh/sketch';
import type {SketchDimensionTopologyChange} from '../../../types/sketch/dimension/SketchDimensionTopologyChangeTypes';
import type {SketchTrimDimensionStateSnapshot} from '../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';

/**
 * 草图剪裁模式枚举。
 */
export enum SketchTrimMode {
    /** 强力剪裁。 */
    Power = 'power',
    /** 角点模式。 */
    Corner = 'corner',
    /** 在内剪除。 */
    TrimAwayInside = 'trim-away-inside',
    /** 在外剪除。 */
    TrimAwayOutside = 'trim-away-outside',
    /** 剪裁到最近端。 */
    TrimToClosest = 'trim-to-closest',
}

/**
 * 单实体模式行为枚举。
 *
 * 设计说明：
 * 1. `TrimToClosest` 是修剪工具中的单击剪裁语义；
 * 2. 当前项目同时复用同一模式承接“延伸实体”命令；
 * 3. 因此需要显式行为枚举区分“剪裁”与“延伸”，避免两条命令共享错误落地逻辑。
 */
export enum SketchTrimSingleEntityBehavior {
    /** 剪裁到最近端。 */
    Trim = 'trim',
    /** 延伸到最近边界。 */
    Extend = 'extend',
}

/**
 * 草图剪裁实体引用。
 */
export interface SketchTrimEntityRef {
    /** 实体 ID。 */
    objectId: string;
    /** 线段索引。 */
    segmentIndex: number | null;
    /** 命中点。 */
    hitPoint?: [number, number, number];
}

/**
 * 草图剪裁选项。
 */
export interface SketchTrimOptions {
    /** 模式。 */
    mode: SketchTrimMode;
    /** 单实体模式行为。 */
    singleEntityBehavior: SketchTrimSingleEntityBehavior;
    /** 是否将剪掉的区段保留为构造几何。 */
    keepTrimmedAsConstruction: boolean;
    /** 是否忽略构造几何。 */
    ignoreConstructionGeometry: boolean;
}

/**
 * 剪裁控制器阶段枚举。
 */
export enum SketchTrimControllerStage {
    /** 空闲态。 */
    Idle = 'idle',
    /** 已激活待操作态。 */
    Armed = 'armed',
    /** 鼠标拖拽执行态。 */
    Dragging = 'dragging',
}

/**
 * 延伸侧枚举。
 */
export enum SketchTrimExtendSide {
    /** 从起点侧延伸。 */
    Start = 'start',
    /** 从终点侧延伸。 */
    End = 'end',
}

/**
 * 端点角色枚举。
 */
export enum SketchTrimEndpointRole {
    /** 起点端。 */
    Start = 'start',
    /** 终点端。 */
    End = 'end',
}

/**
 * 剪裁区间。
 */
export interface SketchTrimInterval {
    /** 区间起点参数。 */
    startT: number;
    /** 区间终点参数。 */
    endT: number;
}

/**
 * 线段区间描述。
 */
export interface SketchTrimSegmentRange {
    /** 起点世界坐标。 */
    start: THREE.Vector3;
    /** 终点世界坐标。 */
    end: THREE.Vector3;
    /** 起点参数。 */
    startT: number;
    /** 终点参数。 */
    endT: number;
}

/**
 * 剪裁实体线段数据。
 */
export interface SketchTrimLineData {
    /** 草图线对象。 */
    line: SketchLineObject;
    /** 段索引。 */
    segmentIndex: number | null;
    /** 起点世界坐标。 */
    start: THREE.Vector3;
    /** 终点世界坐标。 */
    end: THREE.Vector3;
    /** 草图平面法向。 */
    normal: THREE.Vector3;
    /** 是否构造线。 */
    isConstruction: boolean;
}

/**
 * 可参与强力裁剪的草图曲线对象。
 */
export type SketchTrimCurveObject =
    | SketchLineObject
    | SketchArcObject
    | SketchCircleObject
    | SketchEllipseObject
    //------ 样条精确裁剪 开始------
    | SketchParabolaObject
    | SketchSplineObject
    | SketchStyleSplineObject
    //------ 样条精确裁剪 结束------
;

/**
 * 曲线对象类型枚举。
 */
export enum SketchTrimCurveKind {
    /** 直线。 */
    Line = 'line',
    /** 圆弧。 */
    Arc = 'arc',
    /** 圆。 */
    Circle = 'circle',
    /** 椭圆。 */
    Ellipse = 'ellipse',
    //------ 样条精确裁剪 开始------
    /** 抛物线。 */
    Parabola = 'parabola',
    /** 普通样条曲线。 */
    Spline = 'spline',
    /** 样式样条曲线。 */
    StyleSpline = 'style-spline',
    //------ 样条精确裁剪 结束------
}

/**
 * 曲线采样点。
 */
export interface SketchTrimCurveSamplePoint {
    /** 世界坐标。 */
    position: THREE.Vector3;
    /** 目标曲线参数。 */
    parameterT: number;
}

/**
 * 草图曲线裁剪数据。
 */
export interface SketchTrimCurveData {
    /** 目标曲线对象。 */
    curve: SketchTrimCurveObject;
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind;
    /** 段索引。 */
    segmentIndex: number | null;
    /** 草图平面法向。 */
    normal: THREE.Vector3;
    /** 是否构造线。 */
    isConstruction: boolean;
    /** 是否为闭合曲线。 */
    isClosed: boolean;
    /** 采样点序列。 */
    samplePoints: readonly SketchTrimCurveSamplePoint[];
}

/**
 * 剪裁操作结果。
 */
export interface SketchTrimOperationResult {
    /** 是否成功。 */
    success: boolean;
    /** 结果消息。 */
    message: string;
    /** 新建实体 ID 列表。 */
    createdEntityIds: string[];
    /** 删除实体 ID 列表。 */
    removedEntityIds: string[];
    /**
     * 尺寸引用层消费的持久拓扑变化。
     *
     * 运行时 UUID 仍只服务于现有命令内撤销；智能尺寸严禁读取 UUID 推断引用迁移。
     */
    topologyChanges?: SketchDimensionTopologyChange[];
}

/**
 * 屏幕拾取结果。
 */
export interface SketchTrimPickResult extends SketchTrimEntityRef {
    /** 命中的曲线对象。 */
    object: SketchTrimCurveObject;
}

/**
 * 鼠标拖拽状态。
 */
export interface SketchTrimDragState {
    /** 最近一次客户端 X。 */
    lastClientX: number;
    /** 最近一次客户端 Y。 */
    lastClientY: number;
}

/**
 * Inside/Outside 模式选择状态。
 */
export interface SketchTrimInsideOutsideSelectionState {
    /** 第一条边界实体。 */
    boundaryA: SketchTrimEntityRef | null;
    /** 第二条边界实体。 */
    boundaryB: SketchTrimEntityRef | null;
}

/**
 * Corner 模式选择状态。
 */
export interface SketchTrimCornerSelectionState {
    /** 第一条线实体。 */
    firstEntity: SketchTrimEntityRef | null;
}

/**
 * 剪裁控制器快照。
 */
export interface SketchTrimControllerSnapshot {
    /** 当前模式。 */
    mode: SketchTrimMode;
    /** 当前选项。 */
    options: SketchTrimOptions;
    /** 当前阶段。 */
    stage: SketchTrimControllerStage;
}

//------ 强力剪裁撤销记录 开始------
/**
 * 被移除线段的父容器类型枚举。
 */
export enum SketchTrimRemovedLineParentKind {
    /** 父容器为闭合集合对象。 */
    Collection = 'collection',
    /** 父容器为普通 Three 对象。 */
    Object3D = 'object3d',
}

/**
 * 线段状态快照。
 */
export interface SketchTrimLineSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Line;
    /** 线对象。 */
    line: SketchLineObject;
    /** 快照起点对象。 */
    startPoint: SketchPointObject;
    /** 快照终点对象。 */
    endPoint: SketchPointObject;
    /** 快照起点坐标。 */
    startPosition: [number, number, number];
    /** 快照终点坐标。 */
    endPosition: [number, number, number];
    /** 快照构造线标记。 */
    isConstruction: boolean;
}

/**
 * 圆弧状态快照。
 */
export interface SketchTrimArcSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Arc;
    /** 圆弧对象。 */
    line: SketchArcObject;
    /** 快照起点对象。 */
    startPoint: SketchPointObject;
    /** 快照终点对象。 */
    endPoint: SketchPointObject;
    /** 快照圆心点对象。 */
    centerPoint: SketchPointObject;
    /** 快照起点坐标。 */
    startPosition: [number, number, number];
    /** 快照终点坐标。 */
    endPosition: [number, number, number];
    /** 快照圆心坐标。 */
    centerPosition: [number, number, number];
    /** 快照圆弧定义。 */
    definition: Required<SketchArcDefinition>;
    /** 快照构造线标记。 */
    isConstruction: boolean;
}

//------ 强力剪裁圆撤销快照 开始------
/**
 * 圆状态快照。
 *
 * 说明：
 * 1. 强力剪裁整圆时会将圆删除并转换为圆弧；
 * 2. 撤销时必须先恢复原圆对象，再按快照恢复圆心、半径与材质状态；
 * 3. 保留 start/end 字段命名是为了复用强力剪裁标记点定位逻辑。
 */
export interface SketchTrimCircleSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Circle;
    /** 圆对象。 */
    line: SketchCircleObject;
    /** 快照圆心控制点对象。 */
    startPoint: SketchPointObject;
    /** 快照半径端点控制点对象。 */
    endPoint: SketchPointObject;
    /** 快照圆心坐标。 */
    startPosition: [number, number, number];
    /** 快照半径端点坐标。 */
    endPosition: [number, number, number];
    /** 快照半径。 */
    radius: number;
    /** 快照构造线标记。 */
    isConstruction: boolean;
}
//------ 强力剪裁圆撤销快照 结束------

/**
 * 椭圆状态快照。
 */
export interface SketchTrimEllipseSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Ellipse;
    /** 椭圆对象。 */
    line: SketchEllipseObject;
    /** 快照起点对象。 */
    startPoint: SketchPointObject;
    /** 快照终点对象。 */
    endPoint: SketchPointObject;
    /** 快照中心点对象。 */
    centerPoint: SketchPointObject;
    /** 快照起点坐标。 */
    startPosition: [number, number, number];
    /** 快照终点坐标。 */
    endPosition: [number, number, number];
    /** 快照中心点坐标。 */
    centerPosition: [number, number, number];
    /** 快照椭圆定义。 */
    definition: Required<SketchEllipseDefinition>;
    /** 快照构造线标记。 */
    isConstruction: boolean;
}

//------ 样条精确裁剪 开始------
/**
 * 抛物线状态快照。
 */
export interface SketchTrimParabolaSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Parabola;
    /** 抛物线对象。 */
    line: SketchParabolaObject;
    /** 快照抛物线定义。 */
    definition: ReturnType<SketchParabolaObject['getParabolaDefinition']>;
    /** 快照构造线标记。 */
    isConstruction: boolean;
}

/**
 * 样条状态快照。
 */
export interface SketchTrimSplineSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind.Spline | SketchTrimCurveKind.StyleSpline;
    /** 样条对象。 */
    line: SketchSplineObject | SketchStyleSplineObject;
    /** 快照控制点坐标。 */
    controlPointPositions: [number, number, number][];
    /** 快照显示曲线点坐标。 */
    curvePointPositions: [number, number, number][];
    /** 快照曲线元数据。 */
    curveMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>;
    /** 快照构造线标记。 */
    isConstruction: boolean;
}
//------ 样条精确裁剪 结束------

/**
 * 单点父容器恢复快照。
 */
export interface SketchTrimRemovedPointSnapshot {
    /** 点对象。 */
    point: SketchPointObject;
    /** 删除前父容器。 */
    parent: THREE.Object3D | null;
}

/**
 * 强力裁剪更新快照联合类型。
 */
export type SketchTrimUpdatedCurveSnapshot =
    | SketchTrimLineSnapshot
    | SketchTrimArcSnapshot
    | SketchTrimCircleSnapshot
    | SketchTrimEllipseSnapshot
    //------ 样条精确裁剪 开始------
    | SketchTrimParabolaSnapshot
    | SketchTrimSplineSnapshot
    //------ 样条精确裁剪 结束------
;

/**
 * 被删除线段的恢复数据。
 */
export interface SketchTrimRemovedLineSnapshot {
    /** 曲线类型。 */
    curveKind: SketchTrimCurveKind;
    /** 被删除曲线对象。 */
    line: SketchTrimCurveObject;
    /** 删除前父容器。 */
    parent: THREE.Object3D;
    /** 父容器类型。 */
    parentKind: SketchTrimRemovedLineParentKind;
    /** 删除前在集合中的索引。 */
    collectionIndex: number | null;
    /** 删除前相关点父容器快照。 */
    pointSnapshots: SketchTrimRemovedPointSnapshot[];
}

/**
 * 单次强力剪裁撤销记录。
 */
export interface SketchTrimPowerUndoRecord {
    /** 被更新线段快照。 */
    updatedLineSnapshots: SketchTrimUpdatedCurveSnapshot[];
    /** 被删除线段快照。 */
    removedLineSnapshots: SketchTrimRemovedLineSnapshot[];
    /** 本次新建线段 ID 列表。 */
    createdLineIds: string[];
    /** 本次裁剪前的尺寸仓库状态。 */
    dimensionStateBefore: SketchTrimDimensionStateSnapshot | null;
    /** 本次裁剪后的尺寸仓库状态。 */
    dimensionStateAfter: SketchTrimDimensionStateSnapshot | null;
}

/**
 * 强力剪裁标记快照。
 */
export interface SketchTrimPowerMarkerSnapshot {
    /** 对应撤销记录版本号。 */
    undoToken: number;
    /** 标记点世界坐标。 */
    worldPoint: [number, number, number];
}
//------ 强力剪裁撤销记录 结束------
